/**
 * Complete Sprint Dialog Component
 * Handles sprint completion with options for incomplete tasks
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { completeSprint, fetchSprints } from '../../store/sprintSlice';
import { fetchTasks } from '../../store/taskSlice';
import { Sprint, SprintTask } from '../../entities/sprint/model/types';
import './CompleteSprintDialog.scss';

interface CompleteSprintDialogProps {
  open: boolean;
  onClose: () => void;
  sprint: Sprint;
  incompleteTasks?: SprintTask[];
}

type IncompleteTaskAction = 'move-to-backlog' | 'move-to-next-sprint' | 'keep-in-sprint';

const CompleteSprintDialog: React.FC<CompleteSprintDialogProps> = ({
  open,
  onClose,
  sprint,
  incompleteTasks = [],
}) => {
  const dispatch = useAppDispatch();
  const [action, setAction] = useState<IncompleteTaskAction>('move-to-backlog');
  const [loading, setLoading] = useState(false);

  const completedTasks = sprint.completedStoryPoints || 0;
  const totalTasks = sprint.totalStoryPoints || 0;
  const completionRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
  const hasIncompleteTasks = incompleteTasks.length > 0;

  const handleActionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAction(event.target.value as IncompleteTaskAction);
  };

  const handleCompleteSprint = async () => {
    setLoading(true);
    try {
      await dispatch(
        completeSprint({
          sprintId: sprint.id,
          incompleteTaskAction: action,
        })
      ).unwrap();

      if (sprint.boardId) {
        dispatch(fetchTasks({ boardId: String(sprint.boardId) }));
        dispatch(fetchSprints(String(sprint.boardId)));
      }

      onClose();
    } catch (error) {
      console.error('Failed to complete sprint:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="complete-sprint-dialog"
    >
      <DialogTitle className="dialog-header">
        <Box display="flex" alignItems="center" gap={1}>
          <CheckCircleIcon color="success" />
          <Typography variant="h6">Complete Sprint</Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          className="close-button"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="dialog-content">
        {/* Sprint Summary */}
        <Box className="sprint-summary">
          <Typography variant="subtitle1" className="summary-title">
            {sprint.name}
          </Typography>

          <Box className="summary-stats">
            <Box className="stat-item">
              <Typography variant="h3" className="stat-value">
                {completedTasks}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Points Completed
              </Typography>
            </Box>

            <Box className="stat-divider" />

            <Box className="stat-item">
              <Typography variant="h3" className="stat-value">
                {incompleteTasks.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tasks Remaining
              </Typography>
            </Box>

            <Box className="stat-divider" />

            <Box className="stat-item">
              <Typography variant="h3" className="stat-value">
                {completionRate.toFixed(0)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Completion Rate
              </Typography>
            </Box>
          </Box>

          {/* Progress Bar */}
          <Box className="progress-container">
            <LinearProgress
              variant="determinate"
              value={completionRate}
              className={`completion-bar ${
                completionRate >= 75
                  ? 'high'
                  : completionRate >= 50
                  ? 'medium'
                  : 'low'
              }`}
            />
          </Box>
        </Box>

        {/* Incomplete Tasks Warning */}
        {hasIncompleteTasks && (
          <>
            <Alert severity="warning" className="warning-alert">
              <Typography variant="body2" fontWeight={600}>
                This sprint has {incompleteTasks.length} incomplete task
                {incompleteTasks.length > 1 ? 's' : ''}
              </Typography>
              <Typography variant="caption">
                Choose what to do with them before completing the sprint
              </Typography>
            </Alert>

            {/* Incomplete Tasks List */}
            <Box className="incomplete-tasks-section">
              <Typography variant="subtitle2" className="section-title">
                <WarningIcon fontSize="small" />
                Incomplete Tasks ({incompleteTasks.length})
              </Typography>

              <List className="tasks-list">
                {incompleteTasks.slice(0, 5).map((task) => (
                  <ListItem key={task.id} className="task-item">
                    <AssignmentIcon fontSize="small" className="task-icon" />
                    <ListItemText
                      primary={task.name}
                      secondary={`${task.storyPoints || 0} points`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Chip
                      label={task.status}
                      size="small"
                      className="status-chip"
                    />
                  </ListItem>
                ))}
                {incompleteTasks.length > 5 && (
                  <Typography variant="caption" className="more-tasks">
                    +{incompleteTasks.length - 5} more tasks
                  </Typography>
                )}
              </List>
            </Box>

            {/* Action Selection */}
            <Box className="action-selection">
              <Typography variant="subtitle2" className="section-title">
                What should we do with incomplete tasks?
              </Typography>

              <RadioGroup value={action} onChange={handleActionChange}>
                <FormControlLabel
                  value="move-to-backlog"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Move to Backlog
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Tasks will be moved back to the product backlog
                      </Typography>
                    </Box>
                  }
                  className="action-option"
                />

                <FormControlLabel
                  value="move-to-next-sprint"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Move to Next Sprint
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Automatically add to the next sprint when created
                      </Typography>
                    </Box>
                  }
                  className="action-option"
                />

                <FormControlLabel
                  value="keep-in-sprint"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Keep in This Sprint
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Mark sprint as complete but keep tasks here
                      </Typography>
                    </Box>
                  }
                  className="action-option"
                />
              </RadioGroup>
            </Box>
          </>
        )}

        {/* Success Message for 100% completion */}
        {!hasIncompleteTasks && (
          <Alert severity="success" className="success-alert">
            <Typography variant="body2" fontWeight={600}>
              Congratulations! All tasks completed!
            </Typography>
            <Typography variant="caption">
              This sprint achieved 100% completion rate. Great job!
            </Typography>
          </Alert>
        )}

        {/* Sprint Goals Summary */}
        {sprint.goal && (
          <Box className="goals-summary">
            <Typography variant="subtitle2" className="section-title">
              Sprint Goal
            </Typography>
            <Typography variant="body2" className="goal-text">
              {sprint.goal}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions className="dialog-actions">
        <Button onClick={onClose} variant="text" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleCompleteSprint}
          variant="contained"
          color="success"
          startIcon={<CheckCircleIcon />}
          disabled={loading}
          className="complete-button"
        >
          {loading ? 'Completing...' : 'Complete Sprint'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompleteSprintDialog;
